import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, ArrowLeft, Loader2, Check } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { serviceSchema, faqSchema } from "@/utils/schemas";
import FAQSection, { getCityFAQs } from "@/components/FAQSection";
import axios from "axios";
import { useSiteConfig } from "@/context/SiteConfigContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationPage() {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatted_phone, phone_link } = useSiteConfig();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, svcRes] = await Promise.all([
          axios.get(`${API}/locations/${slug}`),
          axios.get(`${API}/cms/services`),
        ]);
        setLocation(locRes.data);
        setServiceCategories(svcRes.data?.categories || []);
      } catch (error) {
        console.error("Error fetching location:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const locationSchemas = useMemo(() => {
    if (!location) return [];
    const s = serviceCategories.map(cat =>
      serviceSchema({
        name: `${cat.title} in ${location.city}`,
        description: `${cat.description} Serving ${location.city}, ${location.county} County, TX.`,
        image: cat.image,
      })
    );
    s.push({
      "@type": "Place",
      name: `${location.city}, Texas`,
      address: {
        "@type": "PostalAddress",
        addressLocality: location.city,
        addressRegion: "TX",
        addressCountry: "US",
      },
    });
    s.push(faqSchema(getCityFAQs(location.city, location.county)));
    return s;
  }, [serviceCategories, location]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Location Not Found</h1>
          <Link to="/locations">
            <Button>View All Locations</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={location.title}
        description={location.meta_description}
        keywords={location.keywords?.join(", ")}
        image={location.image_url}
        schemas={locationSchemas}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Locations", url: "/locations" },
          { name: `${location.city}, TX` },
        ]}
      />
      <div data-testid="location-page" className="pt-20">
        {/* Hero */}
        <section 
          className="py-20 md:py-32 bg-[#1a1a1a] noise-texture relative"
          style={{
            backgroundImage: location.image_url ? `url(${location.image_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {location.image_url && <div className="absolute inset-0 bg-black/70"></div>}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Link to="/locations" className="inline-flex items-center text-gray-400 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Service Areas
            </Link>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#b51a00]" />
              <span className="text-gray-400">{location.county} County, Texas</span>
            </div>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase leading-none mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}
            >
              {location.hero_title || `Junk Removal in ${location.city}, TX`}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
              {location.meta_description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/contact">
                <Button className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg">
                  Get a Free Quote
                </Button>
              </Link>
              <a href={phone_link}>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0f0f0f] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {formatted_phone}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 
                  className="text-3xl font-bold uppercase tracking-tight mb-6"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Junk Removal Services in {location.city}
                </h2>
                <style>{`
                  .location-content h3 {
                    font-family: 'Barlow Condensed', sans-serif;
                    font-size: 1.75rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    color: #0f0f0f;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                  }
                  .location-content h4 {
                    font-family: 'Barlow Condensed', sans-serif;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #b51a00;
                    margin-top: 1.5rem;
                    margin-bottom: 0.75rem;
                  }
                  .location-content p {
                    color: #374151;
                    line-height: 1.75;
                    margin-bottom: 1rem;
                  }
                  .location-content ul {
                    margin-left: 1.25rem;
                    margin-bottom: 1rem;
                  }
                  .location-content li {
                    color: #374151;
                    margin-bottom: 0.5rem;
                  }
                `}</style>
                <div 
                  className="location-content max-w-none"
                  dangerouslySetInnerHTML={{ __html: location.content }}
                />

                {/* Dynamic Services from CMS */}
                <div className="mt-12">
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight mb-6"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Our Services in {location.city}
                  </h3>
                  
                  {serviceCategories.map((cat) => (
                    <div key={cat.slug} className="mb-8">
                      <h4 className="text-lg font-semibold text-[#b51a00] mb-4">
                        {cat.title} in {location.city}
                      </h4>
                      <p className="text-gray-600 mb-4">
                        {location.city} residents and businesses trust Overhaul Ops for professional {cat.title.toLowerCase()}.
                        {location.county && ` Serving all of ${location.county} County.`}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(cat.services || []).map((service) => (
                          <div 
                            key={service.name}
                            className="flex items-start gap-3 p-4 bg-[#f4f4f5] rounded-sm"
                          >
                            <div className="w-6 h-6 bg-[#b51a00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <span className="font-medium">{service.name}</span>
                              <p className="text-sm text-gray-500">{service.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* CTA to Services Page */}
                  <div className="mt-8 p-6 bg-[#1a1a1a] rounded-sm text-center">
                    <p className="text-white mb-4">
                      Ready to get started with junk removal in {location.city}?
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/services">
                        <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                          View All Services
                        </Button>
                      </Link>
                      <Link to="/contact">
                        <Button className="bg-[#b51a00] hover:bg-[#8a1300] text-white">
                          Get a Free Quote
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* About the City */}
                {location.history && (
                  <div className="bg-[#f4f4f5] p-6 rounded-sm">
                    <h3 
                      className="text-xl font-bold uppercase tracking-tight mb-4"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      About {location.city}
                    </h3>
                    <p className="text-gray-600">{location.history}</p>
                  </div>
                )}

                {/* City Highlights */}
                {location.highlights?.length > 0 && (
                  <div className="bg-[#f4f4f5] p-6 rounded-sm">
                    <h3 
                      className="text-xl font-bold uppercase tracking-tight mb-4"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {location.city} Highlights
                    </h3>
                    <ul className="space-y-2">
                      {location.highlights.map((highlight, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-1 text-[#b51a00] flex-shrink-0" />
                          <span className="text-gray-600">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-[#1a1a1a] p-6 rounded-sm text-white">
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight mb-4"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Get a Quote in {location.city}
                  </h3>
                  <p className="text-gray-400 mb-6 text-sm">
                    Fast, professional junk removal from your firefighter-owned local experts.
                  </p>
                  <Link to="/contact" className="block">
                    <Button className="w-full bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm">
                      Request Free Quote
                    </Button>
                  </Link>
                  <a href={phone_link} className="block mt-3">
                    <Button 
                      variant="outline"
                      className="w-full border-white text-white hover:bg-white hover:text-[#1a1a1a]"
                    >
                      <Phone className="mr-2 w-4 h-4" />
                      Call Now
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        {location && (
          <FAQSection
            faqs={getCityFAQs(location.city, location.county)}
            title={`Junk Removal FAQs — ${location.city}`}
            subtitle={`Common questions about our junk removal services in ${location.city}, TX.`}
            cityName={location.city}
          />
        )}

        {/* CTA */}
        <section className="py-20 bg-[#b51a00]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 
              className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Ready for Junk Removal in {location.city}?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get your free, no-obligation quote today!
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg">
                Get Your Free Quote
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
